import React from 'react';
import { useInterpret } from '@xstate/react';
import { InterpreterFrom } from 'xstate';
import editCaseMachine from '../stateMachines/editCase';
import { useBuildfireSettings } from './SettingsContext';
import { useFirebase } from '../services/firebase';
import { useParams } from 'react-router-dom';
import { Case } from '../services/buildfire/rdb/cases';
import { PaymentType } from '../stateMachines/submitCase/selectPaymentType';
import { CaseType } from '../stateMachines/submitCase/caseType';
import { EditCaseDraft } from '../services/sessionStorage/editCaseDraft';

interface EditCaseContext {
  editCaseService: InterpreterFrom<typeof editCaseMachine>;
}

const EditCaseContext = React.createContext<EditCaseContext>(undefined as any);

export const EditCaseContextProvider: React.FC<{
  case: Case;
  targetPage?: string;
  pendingDraft?: EditCaseDraft | null;
}> = ({
  case: c,
  targetPage,
  pendingDraft,
  children,
}) => {
  const { currentUser, app, userMetadata, parentAccount } = useFirebase();
  const { caseId } = useParams<{ caseId: string }>();
  const { settings } = useBuildfireSettings();
  const editCaseService = useInterpret(editCaseMachine, {
    context: {
      caseId,
      firebaseApp: app,
      stripePublishableKey: settings.stripe.publishableKey,
      userId: currentUser?.uid,
      email: currentUser?.email!,
      name: currentUser?.displayName!,
      userMetadata: userMetadata || undefined,
      parentAccount,
      case: c,
      paymentType: c.invoiceAccount
        ? PaymentType.INVOICE
        : PaymentType.CREDIT_CARD,
      caseType: c.priority ? CaseType.PRIORITY : CaseType.STANDARD,
      targetPage,
      pendingDraft,
    },
  });
  (window as any).editCaseService = editCaseService;

  return (
    <EditCaseContext.Provider value={{ editCaseService }}>
      {children}
    </EditCaseContext.Provider>
  );
};

export function useEditCaseContext() {
  return React.useContext(EditCaseContext);
}

export default EditCaseContext;
